import { Link, useRouterState } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Scissors,
  ShoppingCart,
  Ticket,
  Megaphone,
  Tag,
  Hash,
  Layers,
  Users,
  Smartphone,
  Settings,
  LogOut,
  Wallet,
  Lock,
  BadgePercent,
  Scale,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuth } from "@/lib/auth";
import { trpc } from "@/trpc/trpc";
import { PRIVACY_POLICY_URL } from "@/lib/legal";
import { SalonSwitcher } from "@/components/salon-switcher";

const ownerNavItems = [
  { title: "Dashboard", icon: LayoutDashboard, href: "/" },
  { title: "Zabiegi", icon: Scissors, href: "/treatments" },
  { title: "Zakupy", icon: ShoppingCart, href: "/purchases" },
  { title: "Klienci", icon: Users, href: "/clients" },
  { title: "Oferty", icon: Tag, href: "/offers" },
  { title: "Kolekcje", icon: Layers, href: "/collections" },
  { title: "Tagi", icon: Hash, href: "/tags" },
  { title: "Kupony", icon: Ticket, href: "/coupons" },
  { title: "Abonamenty", icon: BadgePercent, href: "/subscriptions" },
  { title: "Notyfikacje", icon: Megaphone, href: "/notifications" },
  { title: "Operatorzy", icon: Smartphone, href: "/operators" },
  { title: "Panel TPay", icon: Wallet, href: "https://panel.tpay.com" },
  { title: "Ustawienia", icon: Settings, href: "/settings" },
] as const;

/** The owner's payments panel follows the salon's provider: a Stripe salon
 *  has its own full Stripe Dashboard, a Tpay salon the Tpay merchant panel. */
const STRIPE_PANEL_ITEM = {
  title: "Panel Stripe",
  icon: Wallet,
  href: "https://dashboard.stripe.com",
} as const;

const operatorNavItems = [
  { title: "Dashboard", icon: LayoutDashboard, href: "/" },
  { title: "Zabiegi", icon: Scissors, href: "/treatments" },
  { title: "Zakupy", icon: ShoppingCart, href: "/purchases" },
  { title: "Klienci", icon: Users, href: "/clients" },
  { title: "Oferty", icon: Tag, href: "/offers" },
  { title: "Kolekcje", icon: Layers, href: "/collections" },
  { title: "Tagi", icon: Hash, href: "/tags" },
  { title: "Kupony", icon: Ticket, href: "/coupons" },
  { title: "Notyfikacje", icon: Megaphone, href: "/notifications" },
] as const;

export function SalonSidebar() {
  const { logout } = useAuth();
  const router = useRouterState();
  const currentPath = router.location.pathname;
  const { data: me } = trpc.meRouter.getMe.useQuery();
  const isAdmin = me?.isAdminOrgMember || me?.isSuperAdmin;
  const isOperator = me?.salonRole === "operator";
  const { data: ownerSalon } = trpc.salon.getOwnerSalon.useQuery(undefined, {
    enabled: !!me && !isOperator,
  });
  // No payments panel until the provider is known: a Stripe salon should
  // never be sent to Tpay while its salon is still loading or failed to.
  const navItems = isOperator
    ? operatorNavItems
    : !ownerSalon
      ? ownerNavItems.filter((item) => item.title !== "Panel TPay")
      : ownerSalon.paymentProvider === "stripe"
        ? ownerNavItems.map((item) =>
            item.title === "Panel TPay" ? STRIPE_PANEL_ITEM : item,
          )
        : ownerNavItems;

  return (
    <Sidebar>
      <SidebarHeader className="border-b px-3 py-2">
        <SalonSwitcher
          subtitle={isOperator ? "Operator" : "Właściciel"}
          isAdmin={!!isAdmin}
          isOperator={isOperator}
        />
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Menu</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems.map((item) => {
                const isExternal = item.href.startsWith("http");
                return (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={
                        isExternal
                          ? false
                          : item.href === "/"
                            ? currentPath === "/"
                            : currentPath.startsWith(item.href)
                      }
                    >
                      {isExternal ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <item.icon />
                          <span>{item.title}</span>
                        </a>
                      ) : (
                        <Link to={item.href as "/"}>
                          <item.icon />
                          <span>{item.title}</span>
                        </Link>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      <SidebarFooter className="border-t">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Lock />
                <span>Polityka prywatności</span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link to="/licenses">
                <Scale />
                <span>Licencje open source</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => {
                logout();
                window.location.href = "/login";
              }}
            >
              <LogOut />
              <span>Wyloguj się</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
